import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Typography, Box, Breadcrumbs, Link, Alert } from '@mui/material';
import { Home } from '@mui/icons-material';
import { EntityCreateForm } from '../components/EntityCreateForm';
import { LoadingSkeleton } from '../components/shared/LoadingSkeleton';
import { useEntityTypeConfig } from '@/hooks';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { isUiConfigEntityPageDisabled } from '@/utils/entityTypeConfig';

export const EntityCreatePage: React.FC = () => {
  const { entityType } = useParams<{ entityType: string }>();
  const navigate = useNavigate();

  const { data: config, isLoading, error, isFetching } = useEntityTypeConfig(entityType!);
  const isEntityPageDisabled = isUiConfigEntityPageDisabled(config?.ui_configuration);

  if (isLoading || isFetching || !config) {
    return <LoadingSkeleton variant="form" count={5} />;
  }

  if (error) {
    return (
      <Container maxWidth={false} sx={{ mt: 2 }}>
        <Alert severity="error">Entity type "{entityType}" not found or not configured.</Alert>
      </Container>
    );
  }

  if (isEntityPageDisabled) {
    return <NotFoundPage />;
  }

  const singularName = config.display_name.endsWith('s')
    ? config.display_name.slice(0, -1)
    : config.display_name;

  return (
    <Container maxWidth={false} sx={{ mt: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Link
            color="inherit"
            href={`/${entityType}`}
            onClick={(e) => {
              e.preventDefault();
              navigate(`/${entityType}`);
            }}
          >
            {config.display_name}
          </Link>
          <Typography color="text.primary">Create {singularName}</Typography>
        </Breadcrumbs>

        <Typography variant="h4" component="h1">
          Create New {singularName}
        </Typography>

        {config.description && (
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            {config.description}
          </Typography>
        )}
      </Box>

      <EntityCreateForm
        entityType={entityType!}
        onSuccess={(entity) => navigate(`/${entityType}/${entity.id}`)}
        onCancel={() => navigate(`/${entityType}`)}
      />
    </Container>
  );
};
